import React, { useState } from "react";
import axios from "axios";
import { RxCrossCircled } from "react-icons/rx";

const CLOUDINARY_UPLOAD_PRESET = "rakesh_kumar_cloudinary";
const CLOUDINARY_CLOUD_NAME = "du5dn8hhw";
const CLOUDINARY_UPLOAD_URL = `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/upload`;

const JobDetailsModal = ({ job, isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    jobLetter: "",
    resume: null,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedFileName, setSelectedFileName] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleFileChange = (e) => {
    if (e.target.files[0]) {
      setFormData({
        ...formData,
        resume: e.target.files[0],
      });
      setSelectedFileName(e.target.files[0].name);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setUploadProgress(0);

    try {
      // First upload the resume to Cloudinary
      let resumeUrl = "";

      if (formData.resume) {
        const fileData = new FormData();
        fileData.append("file", formData.resume);
        fileData.append("upload_preset", CLOUDINARY_UPLOAD_PRESET);

        const uploadResponse = await axios.post(
          CLOUDINARY_UPLOAD_URL,
          fileData,
          {
            onUploadProgress: (progressEvent) => {
              const percentCompleted = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );
              setUploadProgress(percentCompleted);
            },
          }
        );

        resumeUrl = uploadResponse.data.secure_url;
        console.log("Resume uploaded to Cloudinary:", resumeUrl);
      }

      const jobData = {
        jobId: job._id,
        title: job.jobRole,
        company: "BricknBar",
        location: job.location,
        description: job.description,
      };

      const applicationData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        jobLetter: formData.jobLetter,
        resume: resumeUrl,
        jobData: jobData,
      };

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/jobs/create-job-apply`,
        applicationData,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      console.log("Application submitted:", response.data);
      setSuccess(true);
      setFormData({
        name: "",
        email: "",
        phone: "",
        jobLetter: "",
        resume: null,
      });
      setSelectedFileName("");

      setTimeout(() => {
        onClose();
        setSuccess(false);
      }, 2000);
    } catch (err) {
      console.error("Error submitting application:", err);
      setError("Failed to submit application. Please try again.");
    } finally {
      setLoading(false);
      setUploadProgress(0);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-6">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-4 border-b">
          <h2 className="text-xl font-semibold">Job Details</h2>
          <button onClick={onClose} className="hover:text-gray-800">
            <RxCrossCircled size={24} />
          </button>
        </div>

        <div className="p-6">
          {/* Job Details Section */}
          <div className="border rounded-lg p-4 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <h3 className="text-sm text-gray-500">Role</h3>
                <p className="font-medium text-red-600">{job?.jobRole}</p>
              </div>
              <div>
                <h3 className="text-sm text-gray-500">Location</h3>
                <p className="font-medium">{job?.location}</p>
              </div>
              <div>
                <h3 className="text-sm text-gray-500">Job Type</h3>
                <p className="font-medium">{job?.type}</p>
              </div>
            </div>

            <div className="mt-4">
              <h3 className="text-sm text-gray-500">Job Description</h3>
              <p className="mt-1 text-gray-700">{job?.description}</p>
            </div>
          </div>

          {/* Application Form */}
          {success ? (
            <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded">
              Application submitted successfully!
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <h3 className="text-lg font-medium mb-4">
                Apply for the Position
              </h3>

              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-medium mb-2">
                  Your Name
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-medium mb-2">
                  Your Email
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-medium mb-2">
                  Your Phone
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-medium mb-2">
                  Job Letter (Optional)
                </label>
                <textarea
                  name="jobLetter"
                  value={formData.jobLetter}
                  onChange={handleChange}
                  rows="4"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="mb-6">
                <label className="block text-gray-700 text-sm font-medium mb-2">
                  Upload Resume
                </label>
                <div className="relative border border-gray-300 rounded-md">
                  <input
                    type="file"
                    name="resume"
                    accept=".pdf,.doc,.docx,.jpg,.jpeg"
                    onChange={handleFileChange}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    required
                  />
                  <div className="flex items-center justify-between px-3 py-2">
                    <span className="text-gray-500 truncate max-w-xs">
                      {selectedFileName || "No file chosen"}
                    </span>
                    <button
                      type="button"
                      className="bg-gray-200 hover:bg-gray-300 text-gray-800 py-1 px-3 rounded text-sm"
                    >
                      Browse
                    </button>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Accepted formats: PDF, DOC, DOCX, JPG
                </p>
              </div>

              {/* Upload Progress Bar */}
              {loading && uploadProgress > 0 && (
                <div className="mb-4">
                  <div className="w-full bg-gray-200 rounded-full h-2.5">
                    <div
                      className="bg-blue-600 h-2.5 rounded-full"
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Uploading: {uploadProgress}%
                  </p>
                </div>
              )}

              {error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                  {error}
                </div>
              )}

              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={onClose}
                  className="mr-2 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100"
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-blue-300"
                >
                  {loading ? "Submitting..." : "Submit Application"}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default JobDetailsModal;
